/**
 * Display-rounding helpers — keep this the single source of truth on the
 * frontend. Mirrors `backend/lib/rounding.py`.
 *
 * Rounding lives at the estimate level
 * (`estimate.rounding.hours_increment` / `.cost_increment`) and applies to
 * display / export values only. Stored hours and cost remain precise so
 * planner / timeline / audit stay clean.
 */

export const ALLOWED_HOURS_INCREMENTS = [0, 0.5, 1, 5, 10, 25];
export const ALLOWED_COST_INCREMENTS = [0, 1, 10, 50, 100, 500, 1000];

export const HOURS_INCREMENT_OPTIONS = [
  { value: 0,    label: 'Off (show raw)' },
  { value: 0.5,  label: 'Nearest 0.5 hr' },
  { value: 1,    label: 'Nearest 1 hr' },
  { value: 5,    label: 'Nearest 5 hrs' },
  { value: 10,   label: 'Nearest 10 hrs' },
  { value: 25,   label: 'Nearest 25 hrs' },
];

export const COST_INCREMENT_OPTIONS = [
  { value: 0,    label: 'Off (show raw)' },
  { value: 1,    label: 'Nearest $1' },
  { value: 10,   label: 'Nearest $10' },
  { value: 50,   label: 'Nearest $50' },
  { value: 100,  label: 'Nearest $100' },
  { value: 500,  label: 'Nearest $500' },
  { value: 1000, label: 'Nearest $1,000' },
];

export const DEFAULT_ROUNDING = { hours_increment: 0, cost_increment: 0 };

function _coerce(value, allowed) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return 0;
  return allowed.includes(num) ? num : 0;
}

/**
 * Round `value` half-up to the nearest `increment`. Returns `value`
 * unchanged when increment <= 0.
 */
export function roundHalfUp(value, increment) {
  const v = Number(value || 0);
  const inc = Number(increment || 0);
  if (!inc || inc <= 0 || !Number.isFinite(v)) return v;
  // Scale to integer space to avoid float drift at half-way points.
  const scale = 1 / inc;
  return Math.round(v * scale) / scale;
}

export function normaliseRounding(rounding) {
  const r = rounding || {};
  return {
    hours_increment: _coerce(r.hours_increment, ALLOWED_HOURS_INCREMENTS),
    cost_increment: _coerce(r.cost_increment, ALLOWED_COST_INCREMENTS),
  };
}

export function roundHoursForDisplay(hours, rounding) {
  const inc = normaliseRounding(rounding).hours_increment;
  return roundHalfUp(hours, inc);
}

export function roundCostForDisplay(cost, rounding) {
  const inc = normaliseRounding(rounding).cost_increment;
  return roundHalfUp(cost, inc);
}

/** Format hours for display honouring estimate-level rounding. */
export function formatHours(hours, rounding) {
  const rounded = roundHoursForDisplay(hours, rounding);
  const inc = normaliseRounding(rounding).hours_increment;
  // When rounding to 0.5 we keep one decimal; whole-hour rounding shows no decimal.
  const useHalf = inc === 0.5;
  return useHalf
    ? rounded.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : Math.round(rounded).toLocaleString();
}

/** Format cost for display honouring estimate-level rounding. */
export function formatCost(cost, rounding, currencySymbol = '$') {
  const rounded = roundCostForDisplay(cost, rounding);
  const inc = normaliseRounding(rounding).cost_increment;
  // When rounding >= $1 we show no decimals; when off (0) we show 2 decimals.
  if (inc >= 1) {
    return `${currencySymbol}${Math.round(rounded).toLocaleString()}`;
  }
  return `${currencySymbol}${rounded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Resolve which rounding config drives the display for a given estimate
 * in the current user's context.
 *
 *   1. Estimate-level override (`estimate.rounding`) wins whenever either
 *      increment is non-zero — consultants set this explicitly on the
 *      Estimator > Project Factors step.
 *   2. Otherwise, fall back to the user's profile default
 *      (`user.preferences.default_rounding`).
 *   3. Otherwise, no rounding.
 *
 * Always returns a normalised `{hours_increment, cost_increment}`.
 */
export function resolveDisplayRounding(estimate, user) {
  const estRounding = estimate?.rounding || estimate?.results?.rounding || null;
  if (estRounding
      && (Number(estRounding.hours_increment || 0) > 0
          || Number(estRounding.cost_increment || 0) > 0)) {
    return normaliseRounding(estRounding);
  }
  const userDefault = user?.preferences?.default_rounding || null;
  if (userDefault
      && (Number(userDefault.hours_increment || 0) > 0
          || Number(userDefault.cost_increment || 0) > 0)) {
    return normaliseRounding(userDefault);
  }
  return { ...DEFAULT_ROUNDING };
}

/** True when the resolved rounding would actually change the raw value. */
export function isRoundingActive(rounding) {
  const r = normaliseRounding(rounding);
  return r.hours_increment > 0 || r.cost_increment > 0;
}

/** Source label for the "rounded" badge tooltip. */
export function describeRoundingSource(estimate, user) {
  const estRounding = estimate?.rounding || estimate?.results?.rounding || null;
  if (estRounding
      && (Number(estRounding.hours_increment || 0) > 0
          || Number(estRounding.cost_increment || 0) > 0)) {
    return 'this estimate';
  }
  const userDefault = user?.preferences?.default_rounding || null;
  if (userDefault
      && (Number(userDefault.hours_increment || 0) > 0
          || Number(userDefault.cost_increment || 0) > 0)) {
    return 'your profile default';
  }
  return null;
}
